import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import { AlertCircle, CheckCircle, Server, Settings, Loader, Globe, Package, Database, ExternalLink, Info } from 'lucide-react';

const SystemInfo = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [systemInfo, setSystemInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSite, setExpandedSite] = useState(null);

  useEffect(() => {
    if (selectedSite) {
      fetchSystemInfo();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [user, selectedSite]);

  // Auto-expand first site when data loads
  useEffect(() => {
    if (systemInfo.length > 0 && expandedSite === null) {
      setExpandedSite(systemInfo[0].site_id);
    }
  }, [systemInfo]);

  const fetchSystemInfo = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/system-info.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setSystemInfo(result.data);
      } else {
        setError(result.message || 'Failed to fetch system information');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading system information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Server className="h-8 w-8 text-blue-600" />
            System Information
          </h1>
          <p className="text-gray-600 mt-2">Magento version, store structure, and installed extensions</p>
        </div>

        {systemInfo.map((site) => (
          <div key={site.site_id} className="mb-6">
            <div className="bg-white rounded-lg shadow">
              {/* Site Header */}
              <div
                onClick={() => setExpandedSite(expandedSite === site.site_id ? null : site.site_id)}
                className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors rounded-t-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <Server className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{site.site_name}</h3>
                    <p className="text-sm text-gray-500">
                      {site.stats?.total_websites || 0} website(s) • {site.stats?.total_store_views || 0} store view(s)
                      {site.magento_version && ` • ${site.magento_version}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {site.error ? (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
              </div>

              {expandedSite === site.site_id && (
                <div className="border-t border-gray-200">
                  {site.error ? (
                    <div className="p-6">
                      <div className="rounded-lg bg-red-50 p-4 flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-red-900">Error</p>
                          <p className="text-sm text-red-700">{site.error}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 space-y-6">
                      {/* Quick Stats */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Database className="h-4 w-4" />
                          Overview
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Globe className="h-4 w-4 text-blue-600" />
                              <p className="text-xs text-blue-600 uppercase font-medium">Websites</p>
                            </div>
                            <p className="text-2xl font-bold text-blue-900">{site.stats?.total_websites || 0}</p>
                          </div>
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Settings className="h-4 w-4 text-purple-600" />
                              <p className="text-xs text-purple-600 uppercase font-medium">Store Views</p>
                            </div>
                            <p className="text-2xl font-bold text-purple-900">{site.stats?.total_store_views || 0}</p>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Package className="h-4 w-4 text-green-600" />
                              <p className="text-xs text-green-600 uppercase font-medium">Magento</p>
                            </div>
                            <p className="text-2xl font-bold text-green-900">{site.stats?.total_magento_modules || 0}</p>
                          </div>
                          <div className="bg-yellow-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Package className="h-4 w-4 text-yellow-600" />
                              <p className="text-xs text-yellow-600 uppercase font-medium">Amasty</p>
                            </div>
                            <p className="text-2xl font-bold text-yellow-900">{site.stats?.total_amasty_modules || 0}</p>
                          </div>
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Package className="h-4 w-4 text-indigo-600" />
                              <p className="text-xs text-indigo-600 uppercase font-medium">5MS</p>
                            </div>
                            <p className="text-2xl font-bold text-indigo-900">{site.stats?.total_fms_modules || 0}</p>
                          </div>
                          <div className="bg-orange-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Package className="h-4 w-4 text-orange-600" />
                              <p className="text-xs text-orange-600 uppercase font-medium">3rd Party</p>
                            </div>
                            <p className="text-2xl font-bold text-orange-900">{site.stats?.total_third_party_modules || 0}</p>
                          </div>
                        </div>
                      </div>

                      {/* Store Hierarchy Table */}
                      {Array.isArray(site.store_hierarchy) && site.store_hierarchy.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            Store Hierarchy
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Website
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Store Group
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Store View
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Code
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    URL
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {site.store_hierarchy.map((website) => (
                                  website.store_groups.map((group, groupIndex) => (
                                    group.store_views.map((view, viewIndex) => (
                                      <tr key={`${website.id}-${group.id}-${view.id}`} className="hover:bg-gray-50">
                                        {groupIndex === 0 && viewIndex === 0 && (
                                          <td rowSpan={website.store_groups.reduce((sum, g) => sum + g.store_views.length, 0)} className="px-4 py-3 text-sm border-r border-gray-200">
                                            <div>
                                              <div className="flex items-center gap-2">
                                                <p className="font-medium text-gray-900">{website.name}</p>
                                                {website.is_default && (
                                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-medium">
                                                    Default
                                                  </span>
                                                )}
                                              </div>
                                              <p className="text-xs text-gray-500 mt-1">ID: {website.id}</p>
                                            </div>
                                          </td>
                                        )}
                                        {viewIndex === 0 && (
                                          <td rowSpan={group.store_views.length} className="px-4 py-3 text-sm border-r border-gray-200">
                                            <p className="font-medium text-gray-900">{group.name}</p>
                                            <p className="text-xs text-gray-500 mt-1">Root Cat: {group.root_category_id}</p>
                                          </td>
                                        )}
                                        <td className="px-4 py-3 text-sm">
                                          <p className="font-medium text-gray-900">{view.name}</p>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{view.code}</code>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          {view.is_active ? (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                              Active
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                              Inactive
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                          {view.config?.base_url && (
                                            <a
                                              href={view.config.base_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                              title={view.config.base_url}
                                            >
                                              <span className="max-w-[300px] truncate">{view.config.base_url}</span>
                                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                            </a>
                                          )}
                                        </td>
                                      </tr>
                                    ))
                                  ))
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Magento Core Modules */}
                      {Array.isArray(site.magento_modules) && site.magento_modules.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Package className="h-4 w-4 text-green-600" />
                            Magento Core Modules ({site.magento_modules.length})
                          </h4>
                          <div className="bg-green-50 p-4 rounded-lg border border-green-200 max-h-60 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {site.magento_modules.map((module, idx) => (
                                <div key={idx} className="text-xs bg-white p-2 rounded border border-green-200">
                                  <code className="text-gray-700">{module}</code>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Amasty Modules */}
                      {Array.isArray(site.amasty_modules) && site.amasty_modules.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Package className="h-4 w-4 text-yellow-600" />
                            Amasty Extensions ({site.amasty_modules.length})
                          </h4>
                          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 max-h-60 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {site.amasty_modules.map((module, idx) => (
                                <div key={idx} className="text-xs bg-white p-2 rounded border border-yellow-200">
                                  <code className="text-gray-700">{module}</code>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 5MS Modules */}
                      {Array.isArray(site.fms_modules) && site.fms_modules.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Package className="h-4 w-4 text-indigo-600" />
                            5MS Extensions ({site.fms_modules.length})
                          </h4>
                          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 max-h-60 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {site.fms_modules.map((module, idx) => (
                                <div key={idx} className="text-xs bg-white p-2 rounded border border-indigo-200">
                                  <code className="text-gray-700">{module}</code>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Third Party Modules */}
                      {Array.isArray(site.third_party_modules) && site.third_party_modules.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Package className="h-4 w-4 text-orange-600" />
                            Other Third-Party Extensions ({site.third_party_modules.length})
                          </h4>
                          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 max-h-60 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {site.third_party_modules.map((module, idx) => (
                                <div key={idx} className="text-xs bg-white p-2 rounded border border-orange-200">
                                  <code className="text-gray-700">{module}</code>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {site.modules_error && (
                        <div className="rounded-lg bg-yellow-50 p-4 border border-yellow-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-yellow-900">Module list unavailable</p>
                              <p className="text-xs text-yellow-700 mt-1">{site.modules_error}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Info Box */}
                      <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">About this data</p>
                            <p>This information is retrieved directly from your Magento instance via REST API. Click any URL to open the store in a new tab.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SystemInfo;
